import { publicEventRequestConfig } from "./publicEvents";
import { hasCookieConsent } from "@/components/common/CookieConsentBanner";
import { PREVIEW_PARAM } from "@/utils/previewLink";

/**
 * JUB-67: RSVP-form interaction counts.
 *
 * Sent only when the visitor granted analytics consent (the same rule the
 * browser telemetry follows) and never from an owner's preview link. The
 * request carries the stage name and nothing else — no field names, values,
 * tokens or identifiers. Failures are ignored: the counts are best-effort and
 * must never affect the RSVP form, including its draft persistence.
 */

export type RsvpFunnelStage = "opened" | "field_touched" | "draft_restored";

export type RsvpFunnelCounts = {
  opened: number;
  fieldTouched: number;
  draftRestored: number;
  submitted: number;
};

export type RsvpFunnelSummary = {
  days: number;
  enabled: boolean;
  counts: RsvpFunnelCounts;
};

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://dev.wedistar.com/api";

export function canTrackRsvpFunnel(): boolean {
  if (typeof window === "undefined") return false;
  if (new URLSearchParams(window.location.search).has(PREVIEW_PARAM)) return false;
  return hasCookieConsent("analytics");
}

export function trackRsvpFunnel(eventIdentifier: string, stage: RsvpFunnelStage): void {
  if (!canTrackRsvpFunnel()) return;
  try {
    const { headers } = publicEventRequestConfig(eventIdentifier);
    void fetch(`${API_BASE_URL}/events/public/${encodeURIComponent(eventIdentifier)}/rsvp-funnel`, {
      method: "POST",
      keepalive: true,
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ stage }),
    }).catch(() => undefined);
  } catch {
    // Best-effort only.
  }
}

/**
 * Owner totals. The authenticated client is imported lazily so the public
 * RSVP form, which only needs trackRsvpFunnel, does not pull it in.
 */
export async function getRsvpFunnel(eventUuid: string, days = 30): Promise<RsvpFunnelSummary> {
  const { default: apiClient } = await import("./client");
  const response = await apiClient.get<RsvpFunnelSummary | { data: RsvpFunnelSummary }>(
    `/events/${eventUuid}/rsvp-funnel`,
    { params: { days } }
  );
  const body = response.data;
  return "data" in body ? body.data : body;
}
